import { LinearProgress } from '@mui/joy';

import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { useCommitsInfinite } from '@/hooks/useSwr';

import { DiffError } from './Commits/CommitManifestView/DiffError';
import { CommitView } from './Commits/CommitView';
import { CommitsList } from './Commits/CommitsList';
import { useRepoData } from './hooks/useRepoData';

interface CommitsProps {
  inHub: boolean;
  showProxyProviderWarning: boolean;
}

export const Commits = ({ inHub, showProxyProviderWarning }: CommitsProps) => {
  const { owner, repo, repoObj } = useRepoData(inHub);
  const commitsResponse = useCommitsInfinite(owner!, repo!);
  const { commit } = useParams();

  const [showDiff, setShowDiff] = useState(false);

  const lastCommitShortHash = useMemo(
    () => repoObj?.repo.last_commit_hash?.slice(0, 8) ?? '',
    [repoObj?.repo.last_commit_hash]
  );

  const allCommits = useMemo(
    () => commitsResponse.data?.flatMap((page) => page.commits) ?? [],
    [commitsResponse.data]
  );

  const currentCommitIndex = commit
    ? allCommits.findIndex((c) => c.commit_hash.startsWith(commit))
    : null;

  // Load more commits until the current commit is found
  useEffect(() => {
    const totalCommits = commitsResponse.data?.[0]?.total ?? 0;
    const shouldLoadMore =
      commit &&
      currentCommitIndex === -1 &&
      totalCommits > allCommits.length &&
      !commitsResponse.isValidating;

    if (shouldLoadMore) {
      commitsResponse.setSize((size) => size + 1);
    }
  }, [commit, currentCommitIndex, allCommits.length, commitsResponse]);

  const currentCommitObj =
    currentCommitIndex !== null ? allCommits[currentCommitIndex] : undefined;
  const previousCommitObj =
    currentCommitIndex !== null
      ? allCommits[currentCommitIndex + 1]
      : undefined;

  const hasMore = (commitsResponse.data?.[0]?.total ?? 0) > allCommits.length;
  const isNextPageLoading =
    commitsResponse.isValidating &&
    commitsResponse.size > 1 &&
    !!commitsResponse.data?.length;

  if (commitsResponse.isLoading) {
    return (
      <div className="h-full overflow-hidden">
        <LinearProgress />
      </div>
    );
  }

  if (commitsResponse.error) {
    return (
      <div className="flex-grow p-4">
        <ExpandableErrorAlert error={commitsResponse.error} />
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden">
      <div className="flex h-full flex-row overflow-hidden">
        <CommitsList
          currentCommit={commit || lastCommitShortHash}
          commits={allCommits}
          hasMore={hasMore}
          isNextPageLoading={isNextPageLoading}
          setSize={commitsResponse.setSize}
          inHub={inHub}
          setShowDiff={setShowDiff}
        />
        {!isNextPageLoading && currentCommitIndex === -1 ? (
          <div className="flex-grow p-4">
            <DiffError message="Commit not found" />
          </div>
        ) : (
          currentCommitObj &&
          repoObj && (
            <CommitView
              repoObj={repoObj}
              repo={repo!}
              currentCommit={currentCommitObj}
              previousCommit={previousCommitObj}
              showDiff={showDiff}
              setShowDiff={setShowDiff}
              showProxyProviderWarning={showProxyProviderWarning}
            />
          )
        )}
      </div>
    </div>
  );
};
